import { Checkbox, FormControlLabel } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { KycAddress } from "@/types/kycReview";
import { COPY, STEP } from "../constants";
import { attachmentTypeByImage } from "../logic/attachments";
import { getPath, hasValue, isNo, isYes } from "../logic/paths";
import { occupationSlots } from "../logic/occupation";
import { riskProfileDetails } from "../logic/riskProfiler";
import { annualIncomeItems } from "../options";
import { rv } from "../tokens";
import { AlertCircleIcon } from "../ui/icons";
import { StepViewProps } from "./types";

const useStyles = makeStyles((theme) => ({
  card: {
    background: rv.card,
    border: `1.5px solid ${rv.hairline}`,
    borderRadius: rv.radiusCard,
    boxShadow: rv.shadowCard,
    padding: theme.spacing(2, 2.5),
    marginBottom: theme.spacing(2),
  },
  head: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  title: { fontWeight: 700, fontSize: 15, color: rv.heading },
  edit: {
    border: "none",
    background: "none",
    color: rv.green,
    fontWeight: 600,
    fontSize: 13,
    cursor: "pointer",
    font: "inherit",
    textDecoration: "underline",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "200px 1fr",
    gap: theme.spacing(1.5),
    padding: theme.spacing(0.75, 0),
    borderBottom: `1px solid ${rv.hairline3}`,
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr", gap: 2 },
  },
  label: { color: rv.muted, fontSize: 12.5, fontWeight: 600 },
  value: { color: rv.body, fontSize: 13, overflowWrap: "anywhere" },
  confirmWrap: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1.5px solid ${rv.hairline}`,
    borderRadius: rv.radiusCard,
    background: rv.mint,
  },
  error: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    color: rv.errorFg,
    fontSize: 12.5,
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
}));

interface Section {
  title: string;
  editStep: number;
  rows: Array<[string, unknown]>;
}

const FATCA_FOLLOW_UP_ROWS: Array<[string, string]> = [
  ["Are you a U.S. citizen?", "FATCA.usCitizen"],
  ["Are you a U.S. resident?", "FATCA.usResident"],
  ["Are you a U.S. resident alien?", "FATCA.usAlien"],
  ["Do you have a current U.S. telephone number?", "FATCA.usPhone"],
  ["Were you born in the U.S.?", "FATCA.usBorn"],
  ["Do you have a current U.S. residence address?", "FATCA.usAddressResidence"],
  [
    "Do you have a current U.S. mailing address (incl. U.S. P.O.)?",
    "FATCA.usAddressMailing",
  ],
  [
    "Have you issued a standing instruction to transfer funds to a U.S. account?",
    "FATCA.usAccount",
  ],
  [
    "Have you granted power of attorney / signatory authority to a person with a U.S. address?",
    "FATCA.usPOA",
  ],
  [
    'Do you have an "in care of" or "hold mail" address as the sole address for an account?',
    "FATCA.usCareOfAddress",
  ],
];

function formatAddress(address?: KycAddress): string {
  if (!address) return "";
  return [
    address.street,
    address.barangay,
    address.city,
    address.province,
    address.zip,
    address.country,
  ]
    .filter((part) => hasValue(part))
    .join(", ");
}

function display(value: unknown): string {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (isYes(value)) return "Yes";
  if (isNo(value)) return "No";
  return String(value);
}

export default function Review({ state, isTier3, handlers }: StepViewProps) {
  const classes = useStyles();
  const { draft, risk, uploads, confirmed, errors } = state;
  const kyc = draft.kyc;
  const name = kyc.name || {};

  const annualIncome =
    annualIncomeItems.find(
      (item) => item.value === kyc.work?.annualIncomeAmount
    )?.label ?? kyc.work?.annualIncomeAmount;

  const [occupation, position] = occupationSlots(kyc.work?.primaryOccupation);

  // Normalize the investor type to the canonical label (Conservative/Moderate/
  // Aggressive). risk.result is lowercase from computeRiskProfile and the stored
  // user.riskProfile may be any case, so look up by lowercase and fall back to
  // the raw value when it isn't a known profile key.
  const riskValue = risk.result || draft.user.riskProfile;
  const investorType =
    riskProfileDetails[String(riskValue ?? "").toLowerCase()]?.label ??
    riskValue;

  const idEditStep = isTier3 ? STEP.CLIENT : STEP.PROFESSIONAL;

  const sections: Section[] = [
    {
      title: "Personal information",
      editStep: STEP.CLIENT,
      rows: [
        ["First name", name.firstName ?? draft.user.firstName],
        ["Last name", name.lastName ?? draft.user.lastName],
        ["Email address", kyc.email ?? draft.user.email],
        ["Mobile number", draft.user.mobileNumber],
        ["Middle name", name.middleName],
        ["Suffix", name.suffix],
        ["Gender", kyc.gender],
        ["Civil status", kyc.civil?.civilStatus],
        ["Date of birth", kyc.dateOfBirth],
        ["Place of birth", kyc.placeOfBirth],
        ["City of birth", kyc.cityOfBirth],
        ["Nationality", kyc.nationality],
      ],
    },
    {
      title: "Address",
      editStep: STEP.ADDRESS,
      rows: [
        ["Current address", formatAddress(kyc.currentAddress)],
        ...(isTier3
          ? ([
              ["Permanent address", formatAddress(kyc.permanentAddress)],
              ["Work / business address", formatAddress(kyc.businessAddress)],
            ] as Array<[string, unknown]>)
          : []),
      ],
    },
    {
      title: "Government ID",
      editStep: idEditStep,
      rows: [
        ["TIN", kyc.ids?.TIN],
        ["SSS / GSIS", kyc.ids?.SSS],
        [
          "ID expiration",
          kyc.ids?.hasExpiration === false
            ? "This ID does not expire"
            : kyc.ids?.idValidity,
        ],
      ],
    },
    {
      title: "Professional",
      editStep: STEP.PROFESSIONAL,
      rows: isTier3
        ? [
            ["Occupation", occupation],
            ["Position", position],
            ["Employer", kyc.work?.nameOfEmployer],
            ["Nature of business", kyc.work?.businessNature],
            ["Annual income", annualIncome],
          ]
        : [
            ["Occupation", occupation],
            ["Source of funds", kyc.work?.sourceOfFunds],
          ],
    },
    ...(isTier3
      ? ([
          {
            title: "PEP declaration",
            editStep: STEP.PEP,
            rows: [
              ["Works in government", getPath(kyc, "PEP.governmentWork")],
              ["Government relative", getPath(kyc, "PEP.governmentRelative")],
              ["Banks", getPath(kyc, "PEP.banks")],
            ],
          },
          {
            title: "FATCA",
            editStep: STEP.FATCA,
            rows: [
              ["U.S. person", getPath(kyc, "FATCA.usPerson")],
              ...(isYes(getPath(kyc, "FATCA.usPerson"))
                ? FATCA_FOLLOW_UP_ROWS.map(
                    ([label, path]) =>
                      [label, getPath(kyc, path)] as [string, unknown]
                  )
                : []),
            ],
          },
        ] as Section[])
      : []),
    {
      title: "Risk profile",
      editStep: STEP.RISK,
      rows: [["Investor type", investorType]],
    },
    ...(isTier3
      ? ([
          {
            title: "Settlement details",
            editStep: STEP.SETTLEMENT,
            rows: [
              ["Account name", draft.accountDetails.accountName],
              ["Bank", draft.accountDetails.bankName],
              ["Account number", draft.accountDetails.bankAccountNumber],
              [
                "Account type",
                draft.accountDetails.savingsAccount === true
                  ? "Savings Account"
                  : draft.accountDetails.savingsAccount === false
                  ? "Current Account"
                  : "",
              ],
            ],
          },
        ] as Section[])
      : []),
  ];

  const uploadedDocs = Object.entries(uploads).filter(
    ([, entry]) => entry.status === "uploaded"
  );
  if (uploadedDocs.length) {
    sections.push({
      title: "Updated documents",
      editStep: idEditStep,
      rows: uploadedDocs.map(([image, entry]) => [
        attachmentTypeByImage[image]?.name || image,
        entry.name || "Uploaded",
      ]),
    });
  }

  return (
    <div>
      {sections.map((section) => {
        const rows = section.rows.filter(([, value]) => hasValue(value));
        if (!rows.length) return null;
        return (
          <div className={classes.card} key={section.title}>
            <div className={classes.head}>
              <span className={classes.title}>{section.title}</span>
              <button
                type="button"
                className={classes.edit}
                onClick={() => handlers.goToStep(section.editStep)}
              >
                Edit
              </button>
            </div>
            {rows.map(([label, value]) => (
              <div className={classes.row} key={`${section.title}-${label}`}>
                <span className={classes.label}>{label}</span>
                <span className={classes.value}>{display(value)}</span>
              </div>
            ))}
          </div>
        );
      })}

      <div className={classes.confirmWrap}>
        {errors.confirmed && (
          <div className={classes.error}>
            <AlertCircleIcon size={14} />
            {errors.confirmed}
          </div>
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={confirmed}
              color="primary"
              onChange={(event) => handlers.setConfirmed(event.target.checked)}
            />
          }
          label={COPY.confirmLabel}
        />
      </div>
    </div>
  );
}
